import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { useSearch } from "@/utils";

import { LinkedinPersonSuggestionItem } from "@/components/LinkedinPersonSuggestion/LinkedinPersonSuggestion";

import {
	useLazyGetCompanySuggestionQuery,
	useLazyGetPersonSuggestionQuery,
} from "@/pages/Private/redux/liPeople/liPeople.api";

import { useGetProfilesQuery } from "@/pages/Private/redux/liProfileVisits/liProfileVisits.api";

import { GetAllUsersResponse } from "@/types/admin";

export interface LinkedinSuggestions {
	personSuggestions: LinkedinPersonSuggestionItem[] | undefined;
	setPersonSuggestions: Dispatch<SetStateAction<LinkedinPersonSuggestionItem[] | undefined>>;
	setSearchValue: (value: string) => void;
	isLoadingPeople: boolean;
	organizationSuggestions: LinkedinPersonSuggestionItem[] | undefined;
	setOrganizationSuggestions: Dispatch<SetStateAction<LinkedinPersonSuggestionItem[] | undefined>>;
	setCompanySearchValue: (value: string) => void;
	isLoadingCompanies: boolean;
	profiles: GetAllUsersResponse | undefined;
	setProfileSearchValue: (value: string) => void;
}

/**
 * Encapsulates the person / company / scraping-profile autocomplete searches used by the
 * LiPost form. Lives in the parent and is shared between the profile and topic-filter
 * sections (both read the same person/organization suggestion lists).
 */
export const useLinkedinSuggestions = (): LinkedinSuggestions => {
	const { query, setSearchValue } = useSearch();
	const { query: companyQuery, setSearchValue: setCompanySearchValue } = useSearch();
	const { query: profileQuery, setSearchValue: setProfileSearchValue } = useSearch();

	const [personSuggestions, setPersonSuggestions] = useState<LinkedinPersonSuggestionItem[]>();
	const [organizationSuggestions, setOrganizationSuggestions] =
		useState<LinkedinPersonSuggestionItem[]>();

	// Use `isFetching` (not `isLoading`): lazy queries only report `isLoading` on the very
	// first request, but search-as-you-type needs the spinner on every keystroke fetch.
	const [fetchPeople, { isFetching: isLoadingPeople }] = useLazyGetPersonSuggestionQuery();
	const [fetchCompanies, { isFetching: isLoadingCompanies }] = useLazyGetCompanySuggestionQuery();

	const { data: profiles } = useGetProfilesQuery({
		page: 1,
		limit: 20,
		searchValue: profileQuery,
	});

	useEffect(() => {
		if (companyQuery) {
			fetchCompanies({ keyword: companyQuery }).then((results) => {
				const optionValues = results?.data?.suggestions;

				if (optionValues) {
					setOrganizationSuggestions([...optionValues]);
				}
			});
		}
	}, [fetchCompanies, companyQuery]);

	useEffect(() => {
		if (query) {
			fetchPeople({ keyword: query }).then((results) => {
				const optionValues = results?.data?.suggestions;

				if (optionValues) {
					setPersonSuggestions([...optionValues]);
				}
			});
		}
	}, [fetchPeople, query]);

	return {
		personSuggestions,
		setPersonSuggestions,
		setSearchValue,
		isLoadingPeople,
		organizationSuggestions,
		setOrganizationSuggestions,
		setCompanySearchValue,
		isLoadingCompanies,
		profiles,
		setProfileSearchValue,
	};
};
